import React, { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import type { QidUser } from '../api';
import type { QContext } from './qClient';
import type { QLook } from './qField';
import { getTheme, useThemeMode, FONT_UI } from '../theme';
import { useIsMobile } from '../hooks/useIsMobile';
import { qTexts } from './i18n';
import { QMarkdown } from './QMarkdown';
import {
  APP_NAMES, clearAllHistory, EMPTY_HISTORY, fetchQStatus, guessLang, imageToBlock, IMAGE_PLACEHOLDER, isNo, isPromptText,
  isToolResultMessage, isYes, loadHistory, loadSettings, QHttpError, saveHistory, saveSettings, speechText, streamQ, textOf, toolUsesOf,
  type QAppId, type QAppPrompt, type QBlock, type QDone, type QHistory, type QMessage, type QPending, type QRemoteApp, type QSettings, type QToolEvent,
} from './qClient';
import { cachedTtsStatus, getElevenSpeaker, wantsEleven, type TtsStatus } from './elevenSpeaker';
import { detectLanguageSwitch, deviceLang, getQVoice, isEndPhrase, listenWithRetry, type QVoiceLang, type QVoiceState, type QVoiceVoice } from './qVoice';
import { ChatGPTVoiceSession, type RtPending, type RtState } from './chatgptVoice';
import { ChatGPTSettings, QAppsSection, SwitchRow, useQApps } from './QIntegrations';

/**
 * Q-panelen (Q-2): ett blad över ungefär 85 % av skärmen på telefonen och en sidopanel på datorn.
 * Bygger på Q works AssistantPage (Markdown, bilagor, bekräftelsekort, "Ny chatt") med strömning mot
 * Q-motorn (q/qClient.ts), verktygskort, röst (q/qVoice.ts) och inställningar.
 *
 * Rösten: mikrofonen lyssnar en gång, frågan skickas, och svaret läses upp mening för mening medan det
 * strömmar. I samtalsläget lyssnar Q igen när uppläsningen är klar, tills man säger "tack, det var allt",
 * inget hörs eller panelen stängs. Efter en fråga om en ändring (nivå 2) lyssnar Q alltid efter ja eller
 * nej. Nivå 3 (requiresTap) kräver ett tryck på Godkänn, och ett talat ja räcker inte.
 *
 * Andra appar (Q-6): switcharna i inställningarna slår på Qvenari och Qvestium (samtycke i Qid). En app där
 * personen inte har konto visas inte. Påslagna appars prompter blir startkort, och verktygs- och
 * bekräftelsekorten visar vilken app som anropas.
 *
 * ChatGPT-rösten (CV1, chatgptVoice.ts): med röstalternativet ChatGPT startar mikrofonen ett samtal över
 * OpenAI Realtime i stället för lyssna/fråga/läs upp. Samtalet visas i panelen (rtLog) men läggs inte i
 * Claude-historiken – leverantörerna blandas inte. Jakten pausas hela samtalet (onVoiceActivity listening).
 */

type Outcome = 'done' | 'confirm' | 'tap' | 'error' | 'aborted';

interface Attachment { block: QBlock; preview: string }

interface Live { sentences: string[]; tools: QToolEvent[] }

export function QPanel({ open, onClose, listenRequest, askRequest, user, home, variant = 'full', miniBottom, onVoiceActivity, getContext, onLook, huntTab }: {
  open: boolean;
  onClose: () => void;
  /** Ökas av ett långt tryck på Q-knappen: panelen börjar lyssna direkt. */
  listenRequest: number;
  /** RQ4: väckningsordet under jakt ("Q, hur långt …"): frågan ställs och svaret läses upp, även med panelen stängd.
   *  Tom text: Q lyssnar efter frågan. Samtalet syns i panelen när den öppnas. */
  askRequest?: { id: number; text: string } | null;
  user: QidUser;
  /** Samtalets nyckel i historiken (i Qvenari alltid { id: 'qvenari' }). */
  home: { id: string } | null;
  /** mini: ett litet chattkort nere till vänster (under jakt, i samma stil som jaktchatten), utan bakgrund som täcker kartan. */
  variant?: 'full' | 'mini';
  /** Avstånd från nederkanten för mini (ovanför jaktens bottenmeny). */
  miniBottom?: number | string;
  /** Anropas när Q börjar eller slutar lyssna eller prata (jaktsidan pausar då sin transkription). Anropas också
   *  med listening: true innan Q börjar lyssna; ett löfte som returneras väntas in (jakten släpper mikrofonen). */
  onVoiceActivity?: (s: { listening: boolean; speaking: boolean }) => void | Promise<void>;
  /** Position, pågående jakt m.m. som följer med varje anrop (Qvenari, q/qField.ts). */
  getContext?: () => Promise<QContext | undefined> | QContext | undefined;
  /** "Titta": en stillbild från den aktiva kameran, eller null om ingen kamera är på. Utan den visas ingen knapp. */
  onLook?: () => Promise<QLook | null>;
  /** QJ1: under jakt en egen flik med det Q hör (visas först; röstfrågor byter till samtalet). */
  huntTab?: { label: string; badge?: number; content: ReactNode };
}) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const mob = useIsMobile();
  const t = useMemo(() => qTexts(), []);
  const voice = useMemo(() => getQVoice(), []);
  const sub = user.sub;
  const homeId = home?.id ?? null;

  const [settings, setSettingsState] = useState<QSettings>(() => loadSettings(sub));
  const [history, setHistoryState] = useState<QHistory>(() => (homeId ? loadHistory(sub, homeId) : EMPTY_HISTORY));
  const [live, setLive] = useState<Live | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [voiceState, setVoiceState] = useState<QVoiceState>({ listening: false, speaking: false });
  const eleven = useMemo(() => getElevenSpeaker(), []);
  const [elevenSpeaking, setElevenSpeaking] = useState(false);
  const [tts, setTts] = useState<TtsStatus | null>(null);
  const ttsRef = useRef<TtsStatus | null>(null);
  const speakingNow = voiceState.speaking || elevenSpeaking;
  const getContextRef = useRef(getContext);
  getContextRef.current = getContext;
  const onLookRef = useRef(onLook);
  onLookRef.current = onLook;
  /** Senaste "Titta"-bildens adress: följer med som imageUrl så att Q kan koppla den till fällt vilt. */
  const lookUrlRef = useRef<string | undefined>(undefined);
  const onVoiceActivityRef = useRef(onVoiceActivity);
  onVoiceActivityRef.current = onVoiceActivity;
  useEffect(() => { onVoiceActivityRef.current?.({ listening: voiceState.listening, speaking: speakingNow }); }, [voiceState.listening, speakingNow]);
  const [partial, setPartial] = useState('');
  const [view, setView] = useState<'chat' | 'settings' | 'hunt'>(() => (huntTab ? 'hunt' : 'chat'));
  const [status, setStatus] = useState<{ mode: string; used: number; limit: number | null } | null>(null);
  const [canListen, setCanListen] = useState(true);
  const [sessionLang, setSessionLang] = useState<QVoiceLang>(() => (settings.lang === 'auto' ? deviceLang() : settings.lang));
  // ChatGPT-rösten
  const rtRef = useRef<ChatGPTVoiceSession | null>(null);
  const [rtState, setRtState] = useState<RtState>('idle');
  const [rtPending, setRtPending] = useState<RtPending | null>(null);
  const [rtLog, setRtLog] = useState<({ kind: 'msg'; role: 'user' | 'assistant'; text: string } | { kind: 'tool'; id: string; text: string; status: QToolEvent['status'] })[]>([]);
  const rtLogRef = useRef(rtLog);
  rtLogRef.current = rtLog;
  const rtActive = rtState !== 'idle' && rtState !== 'error';

  const historyRef = useRef(history);
  const askingRef = useRef(false);
  const settingsRef = useRef(settings);
  const langRef = useRef(sessionLang);
  const abortRef = useRef<AbortController | null>(null);
  const convoRef = useRef(false);
  const interruptRef = useRef(false);
  const speechGen = useRef(0);
  const speechChain = useRef<Promise<void>>(Promise.resolve());
  const permissionAsked = useRef(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);

  const setHistory = useCallback((h: QHistory) => {
    historyRef.current = h;
    setHistoryState(h);
    if (homeId) saveHistory(sub, homeId, h);
  }, [sub, homeId]);

  const setLang = (l: QVoiceLang) => { langRef.current = l; setSessionLang(l); };

  const setSettings = (s: QSettings) => {
    settingsRef.current = s;
    setSettingsState(s);
    saveSettings(sub, s);
    if (s.lang !== 'auto') setLang(s.lang);
    else setLang(deviceLang());
  };

  // Byte av hem: det hemmets samtal. Läses också om när panelen öppnas, eftersom samma samtal kan
  // föras i en annan panel (Qvenari: toppradens panel och den lilla under jakt).
  useEffect(() => {
    if (!open) return;
    if (askingRef.current) return; // RQ4: ett svar på väckningsordet strömmar redan i det här samtalet
    abortRef.current?.abort();
    const h = homeId ? loadHistory(sub, homeId) : EMPTY_HISTORY;
    historyRef.current = h;
    setHistoryState(h);
    // UX10: inställningarna kan ha ändrats under Integrationer.
    const s = loadSettings(sub);
    settingsRef.current = s;
    setSettingsState(s);
    setError(null);
  }, [sub, homeId, open]);

  useEffect(() => voice.onState(setVoiceState), [voice]);
  useEffect(() => eleven.onSpeaking(setElevenSpeaking), [eleven]);
  // Hämtas direkt (inte först när panelen öppnas): jaktens "Q, …" svarar utan att panelen är öppen.
  useEffect(() => { void cachedTtsStatus().then(st => { ttsRef.current = st; setTts(st); }); }, [open]);
  useEffect(() => { void voice.isAvailable(langRef.current).then(a => setCanListen(a.recognition)).catch(() => setCanListen(false)); }, [voice]);

  useEffect(() => {
    if (!open || !homeId) return;
    void fetchQStatus(homeId).then(s => s && setStatus(s));
  }, [open, homeId]);

  // Apparnas läge: när panelen öppnas och när inställningarna visas (switchen kollar kontot).
  const { apps, appsError, toggleApp } = useQApps(open, view, setNotice);

  useEffect(() => { bottomRef.current?.scrollIntoView({ block: 'end' }); }, [history, live, partial, sending, open, view]);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  }, [draft]);

  // ---------- Uppläsning ----------

  const speak = useCallback((text: string) => {
    const clean = speechText(text);
    if (!clean) return;
    const gen = speechGen.current;
    const lang = settingsRef.current.lang === 'auto' ? guessLang(clean, langRef.current) : langRef.current;
    const s = settingsRef.current;
    // ElevenLabs: hämtningen börjar direkt, uppspelningen i tur och ordning. Misslyckas den läser Apples röst.
    const prepared = wantsEleven(s.engine, ttsRef.current) ? eleven.prepare(clean, lang, s.elevenVoices[lang]) : null;
    speechChain.current = speechChain.current.then(async () => {
      if (gen !== speechGen.current) { prepared?.abort(); return; }
      if (prepared && await eleven.play(prepared)) return;
      if (gen !== speechGen.current) return;
      await voice.speak({ text: clean, lang, voiceId: s.voices[lang] }).catch(() => undefined);
    });
  }, [voice, eleven]);

  const stopVoice = useCallback(() => {
    speechGen.current++;
    convoRef.current = false;
    setPartial('');
    eleven.stop();
    void voice.stop();
  }, [voice, eleven]);

  // Stängd panel: tyst. Ett pågående svar får bli klart och sparas.
  useEffect(() => { if (!open) { stopVoice(); rtRef.current?.stop(); } }, [open, stopVoice]);
  useEffect(() => () => { rtRef.current?.stop(); }, []);
  useEffect(() => () => { speechGen.current++; eleven.stop(); void voice.stop(); }, [voice, eleven]);

  // ---------- Anropen ----------

  const run = useCallback(async (req: { messages: QMessage[]; action?: 'confirm' | 'cancel'; tap?: boolean; prompt?: { app: QAppId; name: string } }, opts: { speak: boolean }): Promise<QDone | null> => {
    if (!homeId) return null;
    const ac = new AbortController();
    abortRef.current?.abort();
    abortRef.current = ac;
    const tools: Record<string, QToolEvent> = {};
    setSending(true);
    setError(null);
    setNotice(null);
    setLive({ sentences: [], tools: [] });
    try {
      const ctx = await Promise.resolve(getContextRef.current?.()).catch(() => undefined);
      const context = ctx || lookUrlRef.current ? { ...(ctx ?? {}), ...(lookUrlRef.current ? { imageUrl: lookUrlRef.current } : {}) } : undefined;
      const done = await streamQ({ homeId, ...req, ...(context ? { context } : {}) }, {
        start: u => setStatus(s => ({ mode: s?.mode ?? 'live', ...u })),
        text: s => {
          setLive(l => (l ? { ...l, sentences: [...l.sentences, s] } : l));
          if (opts.speak) speak(s);
        },
        tool: e => {
          tools[e.id] = e;
          setLive(l => (l ? { ...l, tools: [...l.tools.filter(x => x.id !== e.id), e] } : l));
        },
      }, ac.signal);
      if (ac.signal.aborted) return null;
      const h = historyRef.current;
      // Ett bekräftat anrop behåller kortets fråga som text ("Ska jag bjuda in …?"), med verktygets utfall.
      const merged = { ...h.tools };
      for (const e of Object.values(tools)) merged[e.id] = { text: h.decisions[e.id] && h.tools[e.id] ? h.tools[e.id].text : e.text, status: e.status, ...(e.app ? { app: e.app } : {}) };
      setHistory({
        messages: done.messages,
        tools: merged,
        decisions: h.decisions,
        pending: done.status === 'confirm_required' ? (done.pending ?? []) : null,
      });
      if (done.status === 'error') setError(done.error || t.error);
      if (done.status === 'confirm_required' && opts.speak) {
        for (const p of done.pending ?? []) speak(p.description);
        if (done.pending?.some(p => p.requiresTap)) speak(t.tapSpoken);
      }
      if (settingsRef.current.lang === 'auto' && done.text) setLang(guessLang(done.text, langRef.current));
      return done;
    } catch (err) {
      if (ac.signal.aborted) return null;
      const msg = err instanceof QHttpError ? err.message : err instanceof TypeError ? t.network : (err as Error)?.message || t.error;
      setError(msg);
      if (opts.speak) speak(msg);
      return null;
    } finally {
      if (abortRef.current === ac) {
        abortRef.current = null;
        setSending(false);
        setLive(null);
      }
    }
  }, [homeId, setHistory, speak, t]);

  const outcomeOf = (done: QDone | null): Outcome => {
    if (!done) return 'aborted';
    if (done.status === 'error') return 'error';
    if (done.status === 'confirm_required') return done.pending?.some(p => p.requiresTap) ? 'tap' : 'confirm';
    return 'done';
  };

  const decide = useCallback(async (action: 'confirm' | 'cancel', tap: boolean, opts: { speak: boolean }): Promise<Outcome> => {
    const h = historyRef.current;
    if (!h.pending?.length) return 'done';
    const decisions = { ...h.decisions, ...Object.fromEntries(h.pending.map(p => [p.id, action])) };
    const tools = { ...h.tools, ...Object.fromEntries(h.pending.map(p => [p.id, { text: p.description, status: 'done' as const, ...(p.app ? { app: p.app } : {}) }])) };
    setHistory({ ...h, decisions, tools, pending: null });
    return outcomeOf(await run({ messages: h.messages, action, ...(tap ? { tap: true } : {}) }, opts));
  }, [run, setHistory]);

  /** En fråga, eller ett ja/nej när något väntar på bekräftelse. */
  const handleInput = useCallback(async (text: string, opts: { speak: boolean; images?: Attachment[]; prompt?: QAppPrompt }): Promise<Outcome> => {
    const pending = historyRef.current.pending;
    if (pending?.length && !opts.images?.length) {
      if (isYes(text)) {
        if (pending.some(p => p.requiresTap)) {
          setNotice(t.tapHint);
          if (opts.speak) speak(t.tapSpoken);
          return 'tap';
        }
        return decide('confirm', false, opts);
      }
      if (isNo(text)) return decide('cancel', false, opts);
    }
    // Något annat medan en ändring väntar: den avböjs först, sedan ställs den nya frågan.
    if (pending?.length) await decide('cancel', false, { speak: false });
    if (settingsRef.current.lang === 'auto') setLang(guessLang(text, langRef.current));
    // "Vad tittar jag på?" med en kamera på: en stillbild följer med frågan.
    if (!opts.images?.length && onLookRef.current && /\b(vad (tittar|ser) jag på|vad är det här|titta|what am i looking at|look at this)\b/i.test(text)) {
      const look = await onLookRef.current().catch(() => null);
      if (look) { lookUrlRef.current = look.url; opts = { ...opts, images: [look] }; }
    }
    const content: string | QBlock[] = opts.images?.length ? [...opts.images.map(a => a.block), { type: 'text', text }] : text;
    const messages = [...historyRef.current.messages, { role: 'user' as const, content }];
    setHistory({ ...historyRef.current, messages, pending: null });
    return outcomeOf(await run({ messages, ...(opts.prompt ? { prompt: { app: opts.prompt.app, name: opts.prompt.name } } : {}) }, opts));
  }, [decide, run, setHistory, speak, t]);

  // ---------- Röst ----------

  const waitForSpeech = async () => {
    let chain: Promise<void>;
    do { chain = speechChain.current; await chain; } while (chain !== speechChain.current);
  };

  /** ChatGPT-rösten: ett samtal över OpenAI Realtime (WebRTC) tills man lägger på. */
  const startChatGPT = useCallback(async () => {
    if (rtRef.current || !homeId) return;
    setNotice(null);
    setError(null);
    // Jakten släpper mikrofonen och transkriptionen innan WebRTC tar mikrofonen (QB1); den får tillbaka dem när samtalet slutar.
    await Promise.resolve(onVoiceActivityRef.current?.({ listening: true, speaking: false })).catch(() => undefined);
    const session = new ChatGPTVoiceSession({
      account: settingsRef.current.openaiAccount ?? 'qvenari',
      // Jaktens lilla Q-kort: jaktsamtalet äger ljudsessionen (QB1).
      huntCall: !!onVoiceActivityRef.current,
      background: settingsRef.current.openaiBackground !== false,
      getContext: async () => {
        const ctx = await Promise.resolve(getContextRef.current?.()).catch(() => undefined);
        return ctx || lookUrlRef.current ? { ...(ctx ?? {}), ...(lookUrlRef.current ? { imageUrl: lookUrlRef.current } : {}) } : undefined;
      },
      recent: () => rtLogRef.current.flatMap(x => (x.kind === 'msg' ? [{ role: x.role, text: x.text }] : [])),
      on: {
        state: st => {
          setRtState(st);
          if (st !== 'idle' && st !== 'error') void onVoiceActivityRef.current?.({ listening: true, speaking: st === 'speaking' });
        },
        transcript: (role, text) => setRtLog(l => [...l, { kind: 'msg', role, text }]),
        tool: ({ status, ...e }) => setRtLog(l => (status === 'hidden'
          ? l.filter(x => !(x.kind === 'tool' && x.id === e.id))
          : l.some(x => x.kind === 'tool' && x.id === e.id) ? l.map(x => (x.kind === 'tool' && x.id === e.id ? { ...x, ...e, status } : x)) : [...l, { kind: 'tool', ...e, status }])),
        pending: setRtPending,
        error: msg => setError(msg),
        ended: reason => {
          if (rtRef.current === session) rtRef.current = null;
          if (reason === 'timeout') setNotice(t.rtTimeout);
          void onVoiceActivityRef.current?.({ listening: false, speaking: false });
        },
      },
    });
    rtRef.current = session;
    await session.start();
  }, [homeId, t]);

  /** once (RQ4, väckningsordet): en fråga, sedan tillbaka till jaktens lyssning – inget samtalsläge eller ChatGPT-samtal. */
  const startVoice = useCallback(async (opts?: { once?: boolean }) => {
    if (settingsRef.current.engine === 'openai' && !opts?.once) { await startChatGPT(); return; }
    if (convoRef.current || !homeId) return;
    if (!canListen) { setNotice(t.noRecognition); return; }
    if (!permissionAsked.current && voice.kind === 'native') {
      permissionAsked.current = true;
      const p = await voice.requestPermissions().catch(() => null);
      if (p && (p.microphone === 'denied' || p.speech === 'denied')) { setNotice(t.micDenied); return; }
    }
    convoRef.current = true;
    setNotice(null);
    setError(null);
    try {
      for (let turn = 0; turn < 50 && convoRef.current; turn++) {
        interruptRef.current = false;
        setPartial('');
        // Jakten släpper mikrofonen innan Q:s ljudmotor startar (QB1).
        await Promise.resolve(onVoiceActivityRef.current?.({ listening: true, speaking: false })).catch(() => undefined);
        if (!convoRef.current) return;
        const heard = await listenWithRetry(voice, { lang: langRef.current, onPartial: setPartial });
        setPartial('');
        if (!convoRef.current) return;
        if (heard.reason === 'error' && !heard.text) { setNotice(t.noRecognition); return; }
        const text = heard.text.trim();
        if (!text || isEndPhrase(text)) return;
        const switched = detectLanguageSwitch(text);
        if (switched) setLang(switched);
        const outcome = await handleInput(text, { speak: true });
        await waitForSpeech();
        if (!convoRef.current) return;
        const again = interruptRef.current || outcome === 'confirm' || (!opts?.once && outcome === 'done' && settingsRef.current.conversation);
        if (!again) return;
      }
    } catch (err) {
      setNotice((err as Error)?.message || t.noRecognition);
    } finally {
      convoRef.current = false;
      setPartial('');
      // Startade lyssningen aldrig kommer inget "listening"-byte; jakten ska ändå få tillbaka mikrofonen.
      void onVoiceActivityRef.current?.({ listening: false, speaking: false });
    }
  }, [canListen, handleInput, homeId, startChatGPT, t, voice]);

  const onMic = () => {
    if (settingsRef.current.engine === 'openai') {
      // Medan Q pratar avbryter ett tryck svaret; annars lägger det på (eller ringer upp).
      if (rtRef.current && rtState === 'speaking') { rtRef.current.interrupt(); return; }
      if (rtRef.current) { rtRef.current.stop(); return; }
      void startChatGPT();
      return;
    }
    // Safari läser bara upp efter ett tryck: en tom uppläsning i trycket låser upp de senare.
    if (voice.kind === 'web' && !speakingNow) try { window.speechSynthesis?.speak(new SpeechSynthesisUtterance('')); } catch { /* ingen uppläsning */ }
    if (settingsRef.current.engine === 'elevenlabs' && !speakingNow) eleven.unlock();
    if (voiceState.listening) { void voice.stopListening(); return; }
    if (speakingNow) {
      // Avbryt uppläsningen och lyssna: samtalet fortsätter om det pågår, annars börjar ett nytt.
      speechGen.current++;
      interruptRef.current = true;
      eleven.stop();
      void voice.stop();
      if (!convoRef.current) void startVoice();
      return;
    }
    if (sending) return;
    void startVoice();
  };

  // Långt tryck på Q-knappen.
  const lastListen = useRef(listenRequest);
  useEffect(() => {
    if (listenRequest === lastListen.current) return;
    lastListen.current = listenRequest;
    if (open) { setView('chat'); void startVoice(); }
  }, [listenRequest, open, startVoice]);

  // RQ4: väckningsordet. Jaktens lyssning har redan hört frågan, så den ställs direkt; bara "Q" gör att Q lyssnar
  // (jakten släpper först mikrofonen via onVoiceActivity, regel 1 i QVoicePlugin.swift). Med ChatGPT-rösten svarar
  // Q-motorn och telefonens röst läser upp, eftersom ett Realtime-samtal skulle ta mikrofonen från jakten.
  const lastAsk = useRef(askRequest?.id ?? 0);
  useEffect(() => {
    if (!askRequest || askRequest.id === lastAsk.current) return;
    lastAsk.current = askRequest.id;
    if (!homeId || convoRef.current || rtRef.current) return; // Q lyssnar eller pratar redan
    setView('chat');
    if (!askRequest.text) { void startVoice({ once: true }); return; }
    // Samma samtal kan ha förts i toppradens panel sedan den här senast var öppen.
    if (!open) {
      const h = loadHistory(sub, homeId);
      historyRef.current = h;
      setHistoryState(h);
    }
    stopVoice();
    askingRef.current = true;
    void (async () => {
      try {
        const outcome = await handleInput(askRequest.text, { speak: true });
        if (outcome === 'confirm') { await waitForSpeech(); void startVoice({ once: true }); } // ja eller nej
      } finally {
        askingRef.current = false;
      }
    })();
  }, [askRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- Skrivet ----------

  const submit = (raw: string) => {
    const text = raw.trim();
    if ((!text && !attachments.length) || sending || !homeId) return;
    // Under ett ChatGPT-samtal går det skrivna in i samma samtal.
    if (rtRef.current && text && !attachments.length) { setDraft(''); rtRef.current.sendText(text); return; }
    const images = attachments;
    setDraft('');
    setAttachments([]);
    stopVoice();
    void handleInput(text || t.image, { speak: settingsRef.current.readAloud, images });
  };

  /** Knappen "Titta": en stillbild från kameran och frågan om vad den visar. */
  const look = async () => {
    if (sending || !homeId || !onLookRef.current) return;
    stopVoice();
    const l = await onLookRef.current().catch(() => null);
    if (!l) { setNotice(t.noCamera); return; }
    lookUrlRef.current = l.url;
    void handleInput(t.lookQuestion, { speak: settingsRef.current.readAloud, images: [l] });
  };

  const startPrompt = (p: QAppPrompt) => {
    if (sending || !homeId) return;
    stopVoice();
    void handleInput(p.title, { speak: settingsRef.current.readAloud, prompt: p });
  };

  const onButtonDecision = (action: 'confirm' | 'cancel') => {
    const wasVoice = convoRef.current || speakingNow;
    stopVoice();
    void decide(action, action === 'confirm', { speak: wasVoice || settingsRef.current.readAloud });
  };

  const onAttach = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []).slice(0, 4);
    e.target.value = '';
    try {
      const added = await Promise.all(files.map(f => imageToBlock(f)));
      setAttachments(a => [...a, ...added].slice(0, 4));
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const newChat = () => {
    abortRef.current?.abort();
    rtRef.current?.stop();
    setRtLog([]);
    lookUrlRef.current = undefined;
    stopVoice();
    setHistory(EMPTY_HISTORY);
    setError(null);
    setNotice(null);
    setDraft('');
    setAttachments([]);
    setView('chat');
  };

  // ---------- Utseende ----------

  if (!open) return null;

  const purple = '#6b3fd4';
  const iconBtn: CSSProperties = { width: variant === 'mini' ? 32 : 36, height: 36, borderRadius: 18, border: 'none', background: 'transparent', color: T.textSub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };
  const pill = (primary: boolean): CSSProperties => ({
    padding: '8px 16px', borderRadius: 999, border: primary ? 'none' : `1px solid ${T.border}`, background: primary ? T.accentFill : 'transparent',
    color: primary ? T.onAccent : T.text, fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
  });

  const mini = variant === 'mini';
  const sheet: CSSProperties = mini
    ? { position: 'fixed', left: 12, bottom: miniBottom ?? 'calc(104px + env(safe-area-inset-bottom, 0px))', width: 'min(380px, calc(100vw - 24px))', height: 'min(56vh, 480px)', borderRadius: 12, border: `1px solid ${T.border}`, boxShadow: '0 8px 28px rgba(0,0,0,0.55)' }
    : mob
    ? { position: 'fixed', left: 0, right: 0, bottom: 0, height: '85dvh', borderRadius: '18px 18px 0 0', boxShadow: '0 -8px 30px rgba(0,0,0,0.25)' }
    : { position: 'fixed', top: 0, right: 0, bottom: 0, width: 420, maxWidth: '100vw', borderLeft: `1px solid ${T.border}`, boxShadow: '-8px 0 30px rgba(0,0,0,0.18)' };

  const pending = history.pending;
  const starters = (apps ?? []).filter(a => a.status === 'on').flatMap(a => a.prompts ?? []).slice(0, 6);
  const blocked = sending;
  const listening = voiceState.listening;
  const speaking = speakingNow;
  const empty = !history.messages.length && !live && !rtLog.length && !rtActive;

  const appBadge = (app?: QAppId) => app && (
    <span data-q-app={app} style={{ fontSize: 11, fontWeight: 750, color: '#fff', background: APP_COLORS[app], borderRadius: 999, padding: '1px 7px', flexShrink: 0 }}>{APP_NAMES[app]}</span>
  );

  const toolCard = (key: string, text: string, state: QToolEvent['status'] | 'idle', decision?: 'confirm' | 'cancel', app?: QAppId) => (
    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 11px', borderRadius: 12, background: T.surface2, fontSize: 13, color: T.textSub, alignSelf: 'flex-start', maxWidth: '100%' }}>
      {state === 'running' ? <Spinner color={T.amber} /> : state === 'error' ? <Icon d={ICONS.alert} color={T.danger} size={15} /> : <Icon d={ICONS.check} color={T.success} size={15} />}
      {appBadge(app)}
      <span style={{ minWidth: 0 }}>{text}{state === 'running' ? ' …' : ''}</span>
      {decision && <span style={{ fontSize: 11, fontWeight: 700, color: decision === 'confirm' ? T.success : T.textMuted, marginLeft: 2 }}>{decision === 'confirm' ? t.approved : t.cancelled}</span>}
    </div>
  );

  const renderMessage = (m: QMessage, i: number): ReactNode => {
    if (m.role === 'user') {
      if (isToolResultMessage(m)) return null;
      const blocks = typeof m.content === 'string' ? [{ type: 'text', text: m.content } as QBlock] : m.content.filter(b => b.type !== 'tool_result');
      const images = blocks.filter(b => b.type === 'image') as Extract<QBlock, { type: 'image' }>[];
      const text = blocks.filter(b => b.type === 'text').map(b => (b as { text: string }).text).filter(x => x !== IMAGE_PLACEHOLDER && !isPromptText(x)).join('\n');
      const placeholders = blocks.filter(b => b.type === 'text' && (b as { text: string }).text === IMAGE_PLACEHOLDER).length;
      return (
        <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          {images.length > 0 && <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>{images.map((b, j) => <img key={j} alt={t.image} src={`data:${b.source.media_type};base64,${b.source.data}`} style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 10 }} />)}</div>}
          {placeholders > 0 && <span style={{ fontSize: 12, color: T.textMuted, display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon d={ICONS.image} size={13} color={T.textMuted} />{placeholders > 1 ? `${placeholders} × ${t.image}` : t.image}</span>}
          {text && text !== t.image && <div style={{ maxWidth: '85%', whiteSpace: 'pre-wrap', borderRadius: 18, background: T.amberDim, color: T.text, padding: '9px 14px', fontSize: 15 }}>{text}</div>}
        </div>
      );
    }
    const text = textOf(m.content);
    const uses = toolUsesOf(m.content);
    if (!text && !uses.length) return null;
    return (
      <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {text && <div style={{ fontSize: 15, color: T.text, overflowWrap: 'anywhere', minWidth: 0 }}><QMarkdown text={text} linkColor={T.amber} borderColor={T.border} /></div>}
        {uses.map(u => {
          const info = history.tools[u.id];
          const decision = history.decisions[u.id];
          const isPending = pending?.some(p => p.id === u.id);
          if (isPending) return null;
          return toolCard(u.id, info?.text ?? u.name, info?.status ?? 'done', decision, info?.app ?? appOfName(u.name));
        })}
      </div>
    );
  };

  const confirmCard = (p: QPending[]) => {
    const tap = p.some(x => x.requiresTap);
    return (
      <div role="group" aria-label={tap ? t.confirmTitleTap : t.confirmTitle} style={{ border: `1px solid ${tap ? T.amber : T.border}`, borderRadius: 16, padding: 14, background: T.surface2, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 750, color: tap ? T.amber : T.textSub, display: 'flex', alignItems: 'center', gap: 6 }}>
          {tap && <Icon d={ICONS.lock} size={14} color={T.amber} />}{tap ? t.confirmTitleTap : t.confirmTitle}
        </div>
        {p.map(x => (
          <div key={x.id} style={{ fontSize: 15, color: T.text, background: T.surface, borderRadius: 10, padding: '8px 11px', whiteSpace: 'pre-wrap', display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'flex-start' }}>
            {appBadge(x.app)}{x.description}
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" style={pill(true)} disabled={sending} onClick={() => onButtonDecision('confirm')}>{tap ? t.approve : t.yes}</button>
          <button type="button" style={pill(false)} disabled={sending} onClick={() => onButtonDecision('cancel')}>{tap ? t.cancel : t.no}</button>
        </div>
        <div style={{ fontSize: 12, color: T.textMuted }}>{tap ? t.tapHint : t.confirmHint}</div>
      </div>
    );
  };

  const chat = (
    <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '14px 16px 8px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      {!homeId ? (
        <div style={{ margin: 'auto', textAlign: 'center', color: T.textSub, fontSize: 15, maxWidth: 300 }}>{t.noHome}</div>
      ) : empty ? (
        <div style={{ margin: 'auto 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center', padding: '10px 6px' }}>
          <QMark size={52} />
          <div style={{ fontSize: 21, fontWeight: 750, color: T.text }}>{t.welcome}</div>
          <div style={{ fontSize: 14, color: T.textSub, maxWidth: 320 }}>{t.welcomeSub}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8, marginTop: 6 }}>
            {t.suggestions.map(s => (
              <button key={s} type="button" onClick={() => submit(s)} style={{ padding: '8px 13px', borderRadius: 999, border: `1px solid ${T.border}`, background: T.surface, color: T.text, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>{s}</button>
            ))}
          </div>
          {starters.length > 0 && (
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 750, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.appStart}</div>
              {starters.map(p => (
                <button key={`${p.app}:${p.name}`} type="button" onClick={() => startPrompt(p)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, textAlign: 'left', padding: '10px 13px', borderRadius: 14, border: `1px solid ${T.border}`, background: T.surface, color: T.text, cursor: 'pointer', fontFamily: 'inherit' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700 }}>{appBadge(p.app)}{p.title}</span>
                  {p.description && <span style={{ fontSize: 12.5, color: T.textSub }}>{p.description}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          {history.messages.map(renderMessage)}
          {live && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {live.tools.map(e => toolCard(`live-${e.id}`, e.text, e.status, undefined, e.app))}
              {live.sentences.length > 0 && <div style={{ fontSize: 15, color: T.text, overflowWrap: 'anywhere', minWidth: 0 }}><QMarkdown text={live.sentences.join(' ')} linkColor={T.amber} borderColor={T.border} /></div>}
              {!live.sentences.length && !live.tools.some(e => e.status === 'running') && <Dots color={T.textMuted} label={t.thinking} />}
            </div>
          )}
          {!sending && pending && pending.length > 0 && confirmCard(pending)}
          {rtLog.map((x, i) => (x.kind === 'tool'
            ? toolCard(`rt-${x.id}`, x.text, x.status)
            : x.role === 'user'
              ? <div key={`rt${i}`} style={{ alignSelf: 'flex-end', maxWidth: '85%', whiteSpace: 'pre-wrap', borderRadius: 18, background: T.amberDim, color: T.text, padding: '9px 14px', fontSize: 15 }}>{x.text}</div>
              : <div key={`rt${i}`} style={{ fontSize: 15, color: T.text, overflowWrap: 'anywhere', minWidth: 0 }}>{x.text}</div>))}
          {rtPending && (
            <div data-q-rt-pending role="group" aria-label={rtPending.requiresTap ? t.confirmTitleTap : t.confirmTitle} style={{ border: `1px solid ${rtPending.requiresTap ? T.amber : T.border}`, borderRadius: 16, padding: 14, background: T.surface2, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 750, color: rtPending.requiresTap ? T.amber : T.textSub, display: 'flex', alignItems: 'center', gap: 6 }}>
                {rtPending.requiresTap && <Icon d={ICONS.lock} size={14} color={T.amber} />}{rtPending.requiresTap ? t.confirmTitleTap : t.confirmTitle}
              </div>
              <div style={{ fontSize: 15, color: T.text, background: T.surface, borderRadius: 10, padding: '8px 11px', whiteSpace: 'pre-wrap' }}>{rtPending.question}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" style={pill(true)} onClick={() => rtRef.current?.decide('confirm')}>{rtPending.requiresTap ? t.approve : t.yes}</button>
                <button type="button" style={pill(false)} onClick={() => rtRef.current?.decide('cancel')}>{rtPending.requiresTap ? t.cancel : t.no}</button>
              </div>
              <div style={{ fontSize: 12, color: T.textMuted }}>{rtPending.requiresTap ? t.tapHint : t.confirmHint}</div>
            </div>
          )}
        </>
      )}
      {partial && (
        <div style={{ alignSelf: 'flex-end', maxWidth: '85%', borderRadius: 18, border: `1px dashed ${T.amber}`, color: T.textSub, padding: '9px 14px', fontSize: 15, fontStyle: 'italic' }}>{partial}</div>
      )}
      {error && <div role="alert" style={{ color: T.danger, fontSize: 14 }}>{error}</div>}
      {notice && <div role="status" style={{ color: T.textSub, fontSize: 13, background: T.surface2, borderRadius: 10, padding: '8px 11px' }}>{notice}</div>}
      <div ref={bottomRef} />
    </div>
  );

  const voiceLabel = settings.engine === 'openai' ? t.rtState[rtState] ?? '' : listening ? t.listening : speaking ? t.speaking : sending ? t.thinking : '';
  // ChatGPT-rösten: knappen ringer upp, avbryter (medan Q pratar) eller lägger på.
  const rtMode = settings.engine === 'openai';
  const micOn = rtMode ? rtActive : listening;
  const micLabel = rtMode ? (rtState === 'speaking' ? t.micInterrupt : rtActive ? t.rtHangUp : t.mic) : listening ? t.micStop : speaking ? t.micInterrupt : t.mic;

  const composer = homeId && (
    <div style={{ padding: '8px 12px', paddingBottom: mini ? 8 : mob ? 'calc(10px + env(safe-area-inset-bottom, 0px))' : 14, borderTop: `1px solid ${T.border}` }}>
      {attachments.length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
          {attachments.map((a, i) => (
            <div key={i} style={{ position: 'relative' }}>
              <img src={a.preview} alt={t.image} style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 10, display: 'block' }} />
              <button type="button" aria-label={t.removeAttachment} onClick={() => setAttachments(x => x.filter((_, j) => j !== i))} style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: 10, border: 'none', background: T.text, color: T.surface, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                <Icon d={ICONS.close} size={12} color={T.surface} />
              </button>
            </div>
          ))}
        </div>
      )}
      {voiceLabel && <div aria-live="polite" data-q-voice-label style={{ fontSize: 12, color: micOn ? T.amber : T.textMuted, fontWeight: 650, margin: '0 0 6px 4px' }}>{voiceLabel}</div>}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
        <form onSubmit={e => { e.preventDefault(); submit(draft); }} style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 2, borderRadius: 24, border: `1px solid ${T.border}`, background: T.surface2, padding: 4, minWidth: 0 }}>
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={e => void onAttach(e)} />
          {onLook && (
            <button type="button" aria-label={t.look} title={t.look} disabled={blocked} onClick={() => void look()} style={{ ...iconBtn, width: 38, height: 38 }}>
              <Icon d={ICONS.eye} size={20} color={T.textSub} />
            </button>
          )}
          <button type="button" aria-label={t.attach} title={t.attach} disabled={blocked} onClick={() => fileRef.current?.click()} style={{ ...iconBtn, width: 38, height: 38 }}>
            <Icon d={ICONS.plus} size={20} color={T.textSub} />
          </button>
          <textarea
            ref={textRef}
            rows={1}
            value={draft}
            aria-label={t.placeholder}
            placeholder={listening ? t.listening : t.placeholder}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); submit(draft); } }}
            style={{ flex: 1, minWidth: 0, resize: 'none', border: 'none', outline: 'none', background: 'transparent', color: T.text, fontSize: 16, lineHeight: '22px', padding: '8px 4px', fontFamily: 'inherit', maxHeight: 140 }}
          />
          {(draft.trim() || attachments.length > 0) && (
            <button type="submit" aria-label={t.send} title={t.send} disabled={blocked} style={{ ...iconBtn, width: 38, height: 38, background: T.accentFill, opacity: blocked ? 0.5 : 1 }}>
              <Icon d={ICONS.send} size={18} color={T.onAccent} />
            </button>
          )}
        </form>
        <button
          type="button"
          onClick={onMic}
          disabled={!rtMode && !canListen && !speaking}
          aria-label={micLabel}
          aria-pressed={micOn}
          data-q-rt-state={rtMode ? rtState : undefined}
          title={!rtMode && !canListen ? t.noRecognition : micLabel}
          style={{
            width: 54, height: 54, borderRadius: 27, border: 'none', flexShrink: 0, cursor: 'pointer',
            background: micOn ? '#d63b5b' : purple, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: micOn ? '0 0 0 6px rgba(214,59,91,0.22)' : '0 4px 14px rgba(107,63,212,0.35)', opacity: !rtMode && !canListen && !speaking ? 0.45 : 1,
            transition: 'box-shadow .2s, background .2s',
          }}
        >
          <Icon d={micOn || speaking ? ICONS.stop : ICONS.mic} size={24} color="#fff" fill={micOn || speaking} />
        </button>
      </div>
    </div>
  );

  return (
    <>
      {mob && !mini && <div onClick={onClose} aria-hidden style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.38)', zIndex: 10000 }} />}
      <aside role="dialog" aria-label="Q" aria-modal={mob && !mini} style={{ ...sheet, zIndex: 10001, background: T.surface, color: T.text, display: 'flex', flexDirection: 'column', fontFamily: FONT_UI, overflow: 'hidden', paddingTop: mob || mini ? 0 : 'env(safe-area-inset-top, 0px)' }}>
        {mob && !mini && <div aria-hidden style={{ width: 40, height: 5, borderRadius: 3, background: T.border, margin: '8px auto 0', flexShrink: 0 }} />}
        <header style={{ display: 'flex', alignItems: 'center', gap: mini ? 2 : 8, padding: mini ? '6px 4px 6px 8px' : mob ? '6px 10px 8px 14px' : '12px 12px 10px 16px', borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
          {view === 'settings' ? (
            <button type="button" onClick={() => setView('chat')} aria-label={t.back} style={iconBtn}><Icon d={ICONS.back} size={20} color={T.text} /></button>
          ) : <img src="/qvenari-logo.png" alt={t.title} style={{ height: mini ? 22 : 28, width: 'auto', objectFit: 'contain', flexShrink: 0 }} />}
          {view === 'settings' && <div style={{ fontWeight: 800, fontSize: mini ? 14 : 17 }}>{t.settings}</div>}
          {huntTab && view !== 'settings' && (
            <div role="tablist" aria-label="Q" style={{ display: 'flex', borderRadius: 9, background: T.surface2, padding: 2 }}>
              {([['hunt', huntTab.label], ['chat', 'Fråga']] as const).map(([v, l]) => (
                <button key={v} type="button" role="tab" aria-selected={view === v} onClick={() => setView(v)} style={{
                  position: 'relative', padding: '4px 10px', borderRadius: 7, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700,
                  background: view === v ? T.surface : 'transparent', color: view === v ? T.text : T.textSub,
                }}>
                  {l}
                  {v === 'hunt' && !!huntTab.badge && view !== 'hunt' && <span style={{ marginLeft: 5, fontSize: 10, fontWeight: 800, color: '#fff', background: '#E53935', borderRadius: 999, padding: '0 5px' }}>{huntTab.badge}</span>}
                </button>
              ))}
            </div>
          )}
          {status?.mode === 'mock' && view === 'chat' && <span style={{ fontSize: 11, fontWeight: 700, color: T.amber, background: T.amberDim, borderRadius: 999, padding: '2px 8px' }}>{t.mock}</span>}
          <div style={{ flex: 1 }} />
          {view === 'chat' && homeId && <button type="button" onClick={newChat} aria-label={t.newChat} title={t.newChat} style={iconBtn}><Icon d={ICONS.newChat} size={19} color={T.textSub} /></button>}
          {view === 'chat' && <button type="button" onClick={() => setView('settings')} aria-label={t.settings} title={t.settings} style={iconBtn}><Icon d={ICONS.gear} size={20} color={T.textSub} /></button>}
          <button type="button" onClick={onClose} aria-label={t.close} title={t.close} style={iconBtn}><Icon d={ICONS.close} size={20} color={T.textSub} /></button>
        </header>
        {view === 'settings'
          ? <QSettingsView settings={settings} onChange={setSettings} tts={tts} status={status} apps={apps} appsError={appsError} onToggleApp={(id, on) => void toggleApp(id, on)} onClearHistory={() => { clearAllHistory(sub); newChat(); setView('settings'); setNotice(t.historyCleared); }} notice={notice} />
          : view === 'hunt' && huntTab ? huntTab.content
          : <>{chat}{composer}</>}
      </aside>
    </>
  );
}

// ---------- Inställningar ----------

function QSettingsView({ settings, onChange, tts, status, apps, appsError, onToggleApp, onClearHistory, notice }: {
  settings: QSettings;
  onChange: (s: QSettings) => void;
  tts: TtsStatus | null;
  status: { used: number; limit: number | null } | null;
  apps: QRemoteApp[] | null;
  appsError: boolean;
  onToggleApp: (id: QAppId, on: boolean) => void;
  onClearHistory: () => void;
  notice: string | null;
}) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const t = useMemo(() => qTexts(), []);
  const voice = useMemo(() => getQVoice(), []);
  const eleven = useMemo(() => getElevenSpeaker(), []);
  const [voices, setVoices] = useState<Record<QVoiceLang, QVoiceVoice[]>>({ 'sv-SE': [], 'en-US': [] });
  const [canSpeak, setCanSpeak] = useState(true);

  useEffect(() => {
    void voice.isAvailable('sv-SE').then(a => setCanSpeak(a.synthesis)).catch(() => setCanSpeak(false));
    void Promise.all([voice.getVoices('sv-SE'), voice.getVoices('en-US')])
      .then(([sv, en]) => setVoices({ 'sv-SE': sv.filter(v => !isNoveltyVoice(v)), 'en-US': en.filter(v => !isNoveltyVoice(v)) }))
      .catch(() => undefined);
  }, [voice]);

  const section: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 0', borderBottom: `1px solid ${T.border}` };
  const label: CSSProperties = { fontSize: 13, fontWeight: 750, color: T.textSub, textTransform: 'uppercase', letterSpacing: '0.04em' };
  const hint: CSSProperties = { fontSize: 12.5, color: T.textMuted, lineHeight: 1.4 };
  const langs: QVoiceLang[] = settings.lang === 'auto' ? ['sv-SE', 'en-US'] : [settings.lang];

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '0 18px 24px' }}>
      <div style={section}>
        <div style={label}>{t.language}</div>
        <div role="radiogroup" aria-label={t.language} style={{ display: 'flex', borderRadius: 12, background: T.surface2, padding: 3 }}>
          {([['sv-SE', t.langSv], ['en-US', t.langEn], ['auto', t.langAuto]] as const).map(([v, l]) => (
            <button key={v} type="button" role="radio" aria-checked={settings.lang === v} onClick={() => onChange({ ...settings, lang: v })} style={{
              flex: 1, padding: '8px 0', borderRadius: 9, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 700,
              background: settings.lang === v ? T.surface : 'transparent', color: settings.lang === v ? T.text : T.textSub,
              boxShadow: settings.lang === v ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
            }}>{l}</button>
          ))}
        </div>
        <div style={hint}>{t.langHint}</div>
      </div>

      <div style={section}>
        <div style={label}>{t.engine}</div>
        <div role="radiogroup" aria-label={t.engine} style={{ display: 'flex', borderRadius: 12, background: T.surface2, padding: 3 }}>
          {([['apple', t.engineApple], ['elevenlabs', t.engineEleven], ['openai', t.engineOpenAI]] as const).map(([v, l]) => {
            const active = (settings.engine ?? 'apple') === v;
            const disabled = v === 'elevenlabs' && !tts?.available;
            return (
              <button key={v} type="button" role="radio" aria-checked={active} disabled={disabled} onClick={() => onChange({ ...settings, engine: v })} style={{
                flex: 1, padding: '8px 4px', borderRadius: 9, border: 'none', cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, lineHeight: 1.2,
                background: active ? T.surface : 'transparent', color: active ? T.text : T.textSub, opacity: disabled ? 0.45 : 1,
                boxShadow: active ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
              }}>{l}</button>
            );
          })}
        </div>
        <div style={hint}>{settings.engine === 'openai' ? t.engineOpenAIHint : tts && !tts.available ? t.engineUnavailable : t.engineHint}</div>
      </div>

      {settings.engine === 'openai' ? <ChatGPTSettings settings={settings} onChange={onChange} /> : settings.engine === 'elevenlabs' && tts?.available ? (
        <div style={section}>
          <div style={label}>{t.voice}</div>
          {!tts.voices.length ? <div style={hint}>{tts.error ?? t.elevenLoading}</div> : langs.map(l => (
            <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <select
                aria-label={l === 'sv-SE' ? t.voiceSv : t.voiceEn}
                value={settings.elevenVoices?.[l] ?? ''}
                onChange={e => onChange({ ...settings, elevenVoices: { ...settings.elevenVoices, [l]: e.target.value || undefined } })}
                style={{ flex: 1, minWidth: 0, padding: '9px 10px', borderRadius: 10, border: `1px solid ${T.border}`, background: T.surface2, color: T.text, fontSize: 14, fontFamily: 'inherit' }}
              >
                <option value="">{`${l === 'sv-SE' ? t.voiceSv : t.voiceEn}: ${t.voiceDefault}`}</option>
                {tts.voices.map(v => <option key={v.id} value={v.id}>{v.description ? `${v.name} (${v.description})` : v.name}</option>)}
              </select>
              <button type="button" onClick={() => { eleven.stop(); eleven.unlock(); void eleven.play(eleven.prepare(l === 'sv-SE' ? 'Hej! Jag är Q.' : 'Hi! I am Q.', l, settings.elevenVoices?.[l])); }} style={{ padding: '8px 12px', borderRadius: 999, border: `1px solid ${T.border}`, background: 'transparent', color: T.text, fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}>{t.voiceTry}</button>
            </div>
          ))}
        </div>
      ) : (
      <div style={section}>
        <div style={label}>{t.voice}</div>
        {!canSpeak ? <div style={hint}>{t.voiceNone}</div> : langs.map(l => (
          <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select
              aria-label={l === 'sv-SE' ? t.voiceSv : t.voiceEn}
              value={settings.voices[l] ?? ''}
              onChange={e => onChange({ ...settings, voices: { ...settings.voices, [l]: e.target.value || undefined } })}
              style={{ flex: 1, minWidth: 0, padding: '9px 10px', borderRadius: 10, border: `1px solid ${T.border}`, background: T.surface2, color: T.text, fontSize: 14, fontFamily: 'inherit' }}
            >
              <option value="">{`${l === 'sv-SE' ? t.voiceSv : t.voiceEn}: ${t.voiceDefault}`}</option>
              {voices[l].map(v => <option key={v.id} value={v.id}>{`${v.name}${v.quality !== 'default' ? ` (${v.quality})` : ''}`}</option>)}
            </select>
            <button type="button" onClick={() => void voice.speak({ text: l === 'sv-SE' ? 'Hej! Jag är Q.' : 'Hi! I am Q.', lang: l, voiceId: settings.voices[l] })} style={{ padding: '8px 12px', borderRadius: 999, border: `1px solid ${T.border}`, background: 'transparent', color: T.text, fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}>{t.voiceTry}</button>
          </div>
        ))}
      </div>
      )}

      <div style={section}>
        <SwitchRow label={t.conversation} hint={t.conversationHint} on={settings.conversation} onToggle={() => onChange({ ...settings, conversation: !settings.conversation })} />
        <SwitchRow label={t.readAloud} hint={t.readAloudHint} on={settings.readAloud} onToggle={() => onChange({ ...settings, readAloud: !settings.readAloud })} />
      </div>

      {/* Qvenari har inga andra appar i Q än (API:t svarar med en tom lista): då döljs avsnittet. UX10: samma avsnitt under Integrationer. */}
      <QAppsSection apps={apps} appsError={appsError} onToggleApp={onToggleApp} hideWhenEmpty />

      <div style={{ ...section, borderBottom: 'none' }}>
        {status && <div style={hint}>{t.usage(status.used, status.limit)}</div>}
        <button type="button" onClick={() => { if (window.confirm(t.clearHistoryConfirm)) onClearHistory(); }} style={{ alignSelf: 'flex-start', padding: '9px 16px', borderRadius: 999, border: `1px solid ${T.danger}`, background: 'transparent', color: T.danger, fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>{t.clearHistory}</button>
        {notice && <div role="status" style={hint}>{notice}</div>}
      </div>
    </div>
  );
}

/** macOS skämtröster (Bahh, Boing, Zarvox …), också med svenska namn. Passar inte för Q. */
const NOVELTY = /\b(albert|bad news|bahh|bells|boing|bubbles|cellos|deranged|good news|hysterical|jester|organ|pipe organ|superstar|trinoids|whisper|wobble|zarvox|bjällror|bubblor|viskning|orgel|cellon|dåliga nyheter|goda nyheter|gyckel|gycklare|superstjärna|vingel)\b/i;
function isNoveltyVoice(v: QVoiceVoice): boolean {
  return NOVELTY.test(v.name) || /speech\.synthesis\.voice\.(Albert|BadNews|Bahh|Bells|Boing|Bubbles|Cellos|Deranged|GoodNews|Hysterical|Organ|Princess|Superstar|Trinoids|Whisper|Zarvox)/.test(v.id);
}

// ---------- Små delar ----------

const APP_COLORS: Record<QAppId, string> = { qvenari: '#4a6b3a', qvestium: '#b0406a' };

const appOfName = (name: string): QAppId | undefined => (name.startsWith('qvenari__') ? 'qvenari' : name.startsWith('qvestium__') ? 'qvestium' : undefined);

/** Det lila Q-märket. */
export function QMark({ size = 30 }: { size?: number }) {
  return (
    <span aria-hidden style={{ width: size, height: size, borderRadius: size / 2, background: '#6b3fd4', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: size * 0.5, flexShrink: 0, lineHeight: 1 }}>Q</span>
  );
}

const ICONS = {
  mic: 'M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3zM5 11a7 7 0 0 0 14 0M12 18v3M8.5 21h7',
  stop: 'M7 7h10v10H7z',
  send: 'M12 19V5M5 12l7-7 7 7',
  plus: 'M12 5v14M5 12h14',
  eye: 'M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12zM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z',
  close: 'M6 6l12 12M18 6L6 18',
  gear: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z',
  newChat: 'M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z',
  back: 'M15 18l-6-6 6-6',
  check: 'M5 12.5l4.5 4.5L19 7',
  alert: 'M12 8v5M12 16.5v.5M10.3 3.9L2.4 17.5A2 2 0 0 0 4.1 20.5h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z',
  lock: 'M6 11h12v9H6zM8.5 11V8a3.5 3.5 0 0 1 7 0v3',
  image: 'M4 5h16v14H4zM4 16l5-5 4 4 3-3 4 4M15.5 9.5h.01',
};

function Icon({ d, size = 20, color, fill }: { d: string; size?: number; color: string; fill?: boolean }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={fill ? color : 'none'} stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flexShrink: 0 }}>
      <path d={d} />
    </svg>
  );
}

function Spinner({ color }: { color: string }) {
  return (
    <span aria-hidden style={{ width: 14, height: 14, borderRadius: 7, border: `2px solid ${color}`, borderTopColor: 'transparent', display: 'inline-block', animation: 'qspin 0.8s linear infinite', flexShrink: 0 }}>
      <style>{'@keyframes qspin{to{transform:rotate(360deg)}}'}</style>
    </span>
  );
}

function Dots({ color, label }: { color: string; label: string }) {
  return (
    <div role="status" aria-label={label} style={{ display: 'flex', gap: 5, padding: '4px 0' }}>
      <style>{'@keyframes qdot{0%,80%,100%{opacity:.25}40%{opacity:1}}'}</style>
      {[0, 160, 320].map(d => <span key={d} style={{ width: 7, height: 7, borderRadius: 4, background: color, animation: `qdot 1.2s ${d}ms infinite` }} />)}
    </div>
  );
}
